import { round } from "lodash";
import type { Dispatch, FC, SetStateAction } from "react";
import type { SeamGeoData, StratumGeoData } from "@/store/geo-test/types";
import { SEAM_BG, STRATUM_BG } from "../../base";
import type { GeoLayerCoordinates } from "../../types";
import { getHalfPath, getPath } from "../../utils";
import { positionTranslate, SEAM_KEYS, SEAM_NAMING_MAP } from "./const";
import type { Position, TooltipProps } from "./types";

type GeoLayerComponentProps = GeoLayerCoordinates & {
	colorStart?: string;
	colorEnd?: string;
	half?: boolean;
	geoObject: SeamGeoData | StratumGeoData;
};

type TooltipInfo = {
	nextGeoObject?: SeamGeoData | StratumGeoData;
	position?: Position;
	isLastWell?: boolean;
	setTooltipInfo: Dispatch<SetStateAction<TooltipProps>>;
};

type TooltipEvents = {
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	onMouseMove: (event: React.MouseEvent<SVGPathElement>) => void;
	onMouseMoveWithoutPosition: () => void;
};

export const GeoLayerComponent: FC<GeoLayerComponentProps & TooltipInfo> = (
	props,
) => {
	const {
		startX,
		startTop,
		endX,
		endTop,
		half = false,
		geoObject,
		nextGeoObject,
		setTooltipInfo,
		isLastWell,
	} = props;
	//считает позиционирование для второй половины фигуры
	const halfPosition: Position = isLastWell
		? `left${(startTop < endTop && half) || (startTop > endTop && !half) ? "Top" : "Bottom"}`
		: "right";

	const handleMouseEnter = () => {
		setTooltipInfo({
			x: startX,
			y: startTop,
			geoObject,
			position: half && startX > endX ? halfPosition : "leftBottom",
		});
	};

	//когда при наведении на фигуру ее перекрывает тултип от соседа, то mouseEnter не отработает, отработает это событие
	const handleMouseMoveWithoutPosition = () => {
		setTooltipInfo((prevState) => {
			if (
				prevState.position ===
				(half && startX > endX ? halfPosition : "leftBottom")
			) {
				return prevState;
			}
			return {
				x: startX,
				y: startTop,
				geoObject,
				position: half && startX > endX ? halfPosition : "leftBottom",
			};
		});
	};

	const handleMouseLeave = () => {
		setTooltipInfo((prevState) => ({
			...prevState,
			geoObject: null,
			position: undefined,
		}));
	};

	const handleMouseMove = (event: React.MouseEvent<SVGPathElement>) => {
		const svg = event.currentTarget.ownerSVGElement;
		if (!svg) return;

		const rect = svg.getBoundingClientRect();
		const cursorX = event.clientX - rect.left; // Переводим координаты в систему SVG
		const middleX = (startX + endX) / 2;

		if (cursorX < middleX) {
			setTooltipInfo((prevState) => {
				if (prevState.x === startX && prevState.y === startTop) {
					return prevState;
				}
				return { x: startX, y: startTop, geoObject, position: "leftBottom" };
			});
		} else {
			setTooltipInfo((prevState) => {
				if (prevState.x === endX && prevState.y === endTop) {
					return prevState;
				}
				return {
					x: endX,
					y: endTop,
					geoObject: nextGeoObject ?? geoObject,
					position: halfPosition,
				};
			});
		}
	};

	return (
		<GeoObjectComponent
			{...props}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onMouseMove={handleMouseMove}
			onMouseMoveWithoutPosition={handleMouseMoveWithoutPosition}
		/>
	);
};

const GeoObjectComponent: FC<GeoLayerComponentProps & TooltipEvents> = ({
	startX,
	startTop,
	startBottom,
	endX,
	endTop,
	endBottom,
	colorStart,
	colorEnd,
	geoObject,
	half = false,
	onMouseEnter,
	onMouseLeave,
	onMouseMove,
	onMouseMoveWithoutPosition,
}) => {
	const isSeam = "saturant" in geoObject;
	const pathData = half
		? getHalfPath(
				{ startX, startTop, startBottom, endX, endTop, endBottom },
				isSeam,
			)
		: getPath({ startX, startTop, startBottom, endX, endTop, endBottom });
	const key = `${startX}${startTop}${startBottom}${endX}${endTop}${endBottom}`;
	const BG_COLOR = isSeam ? SEAM_BG : STRATUM_BG;
	const generalColor = colorStart ?? BG_COLOR;

	if (half || !isSeam) {
		return (
			<path
				d={pathData}
				key={`path-${key}`}
				fill={generalColor}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				onMouseMove={onMouseMoveWithoutPosition}
			/>
		);
	}

	return (
		<>
			<defs>
				<linearGradient id={`grad-${key}`} x1="0%" y1="0%" x2="100%" y2="0%">
					<stop offset="0%" stopColor={generalColor} />
					<stop offset="48%" stopColor={generalColor} />
					<stop offset="52%" stopColor={colorEnd ?? generalColor} />
					<stop offset="100%" stopColor={colorEnd ?? generalColor} />
				</linearGradient>
				<mask id={`mask-${key}`}>
					<path key={`path-${key}`} d={pathData} fill="white" />
				</mask>
			</defs>
			<rect
				x={startX}
				y={Math.min(startTop, endTop)}
				height={Math.max(startBottom, endBottom) - Math.min(startTop, endTop)}
				width={endX - startX}
				fill={`url(#grad-${key})`}
				mask={`url(#mask-${key})`}
				style={{ pointerEvents: "none" }}
			/>
			<path
				d={pathData}
				key={`path-${key}`}
				fill="transparent"
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				onMouseMove={onMouseMove}
			/>
		</>
	);
};

export const GeoLayerTooltip: FC<TooltipProps> = ({
	geoObject,
	x,
	y,
	position,
}) => {
	if (!geoObject) {
		return null;
	}

	const isSeam = "saturant" in geoObject;

	return (
		<div
			className="absolute whitespace-nowrap p-2 text-xs text-white"
			style={{
				top: y,
				left: x,
				backgroundColor: "rgba(0, 0, 0, 0.7)",
				transform: positionTranslate[position ?? "leftBottom"],
				borderRadius: "4px",
			}}
		>
			<div>
				Глубина - {`${round(geoObject.start, 2)} - ${round(geoObject.end, 2)}`}
			</div>
			<div>Толщина - {round(geoObject.end - geoObject.start, 2)}</div>
			{isSeam && (
				<>
					<div>Насыщение - {geoObject.saturant.title}</div>
					{SEAM_KEYS.filter((key) => geoObject[key]).map((key) => (
						<div key={key}>
							{SEAM_NAMING_MAP[key]} - {round(geoObject[key] ?? 0, 2)}
						</div>
					))}
				</>
			)}
		</div>
	);
};
